<script lang="ts">
  import { api } from '$shared/api';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  interface SuggestedUser {
    id: number;
    username: string;
    avatar_url: string | null;
  }

  let suggestions = $state<SuggestedUser[]>([]);
  let loading = $state(true);
  let followingIds = $state<Set<number>>(new Set());
  let followLoadingIds = $state<Set<number>>(new Set());

  $effect(() => {
    api
      .get<{ suggestions: SuggestedUser[] }>('/users/current/suggestions')
      .then((d) => {
        suggestions = d.suggestions || [];
        loading = false;
      })
      .catch((e: unknown) => {
        console.error('Failed to load suggestions:', e);
        loading = false;
      });
  });

  async function toggleFollow(user: SuggestedUser) {
    if (followLoadingIds.has(user.id)) return;
    followLoadingIds = new Set([...followLoadingIds, user.id]);
    try {
      if (followingIds.has(user.id)) {
        await api.delete(`/follow/${user.id}`);
        const next = new Set(followingIds);
        next.delete(user.id);
        followingIds = next;
      } else {
        await api.post('/follow', { followee_id: user.id });
        followingIds = new Set([...followingIds, user.id]);
      }
    } catch (e) {
      console.error('Failed to toggle follow:', e);
    }
    const next = new Set(followLoadingIds);
    next.delete(user.id);
    followLoadingIds = next;
  }
</script>

<div>
  <h1>Suggested Players</h1>
  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else if suggestions.length === 0}
    <p class="empty">No suggestions right now. Check back later!</p>
  {:else}
    <div class="suggestions-list">
      {#each suggestions as user}
        <div class="suggestion-row">
          <a href="#/users/{user.username}" class="suggestion-profile">
            <Avatar username={user.username} avatarUrl={user.avatar_url} size="sm" />
            <div class="suggestion-info">
              <span class="suggestion-name">{user.username}</span>
            </div>
          </a>
          <button
            class="btn {followingIds.has(user.id) ? 'btn-outline btn-unfollow' : 'btn-primary'} btn-pill btn-sm"
            onclick={() => toggleFollow(user)}
            disabled={followLoadingIds.has(user.id)}
          >
            {followingIds.has(user.id) ? 'Following' : 'Follow'}
          </button>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  h1 {
    margin-bottom: 1.5rem;
  }
  .empty {
    color: var(--text-secondary);
  }
  .suggestions-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .suggestion-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .suggestion-profile {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    color: var(--text);
    flex: 1;
    min-width: 0;
  }
  .suggestion-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .suggestion-name {
    font-weight: 600;
  }
  .btn-sm {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
  }
  .btn-unfollow:hover {
    border-color: var(--error);
    color: var(--error);
  }
</style>
